import React, { useEffect, useState, useCallback } from 'react';
import { FlatButton, Modal, Spacing, Text, Button } from '@reservamos/elements';
import { Trans, useTranslation } from 'react-i18next';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import PropTypes from 'prop-types';
import cookie from 'js-cookie';
import { trackEvent } from 'user-analytics';
import {
  APP_DOWNLOAD_MODAL_CONTINUE_IN_BROWSER,
  APP_DOWNLOAD_MODAL_CONTINUE_IN_APP,
  APP_DOWNLOAD_MODAL_CLOSED,
  APP_DOWNLOAD_MODAL_SHOWED,
} from 'constants/TrackEvents';
import defaultModalImage from 'images/brands-unique/cds/modal-app.png';
import defaultDiscountModalImage from 'images/brands-unique/cds/discount.png';
import useGrowthBookFeaturesValue from 'components/GrowthBookProvider/useGrowthBookFeaturesValues';
import useIsMobile from 'hooks/useIsMobile';
import PromoDownloadModalMessage, {
  getAppDownloadModalImageUrl,
  isPromoDownloadModalActive,
} from './promoDownloadModalMessage';

const COOKIE_NAME = 'appDownloadModalClosed';
const MODAL_ID = 'app-download-modal'; // Used to hide the modal in webviews

/**
 * AppDownloadModal component.
 * Render a modal prompting the user to download the app.
 * @param {object} props - hideDialog.
 * @param {boolean} props.hideDialog - Whether to hide the modal.
 * @returns {JSX.Element|null}
 */
const AppDownloadModal = ({ hideDialog }) => {
  const { t } = useTranslation('general');
  const { smartBanner, downloadAppImage, promoDownloadModal } = useWhitelabelEnvs();
  const { SHOW_DOWNLOAD_APP_MODAL } = useWhitelabelFeatures();
  const [platform, setPlatform] = useState('android'); // Default to Android
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(true);
  const { showDownloadAppModal } = useGrowthBookFeaturesValue('show_download_app_modal');

  // Detect platform on mount
  useEffect(() => {
    /**
     * Detects the user's platform (iOS or Android) and updates state.
     */
    const detectPlatform = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        setPlatform('ios');
      } else {
        setPlatform('android');
      }
    };
    detectPlatform();
  }, []);

  // Check if modal was previously closed via cookie
  const isModalPreviouslyClosed = Boolean(cookie.get(COOKIE_NAME));

  const shouldShowModal =
    !hideDialog &&
    isMobile &&
    SHOW_DOWNLOAD_APP_MODAL &&
    !isModalPreviouslyClosed &&
    isVisible &&
    showDownloadAppModal;

  // Track modal shown event when modal is actually displayed
  useEffect(() => {
    if (shouldShowModal) {
      trackEvent(APP_DOWNLOAD_MODAL_SHOWED);
    }
  }, [shouldShowModal]);

  /**
   * Handle closing the modal with X or close button.
   */
  const closeModal = useCallback(() => {
    cookie.set(COOKIE_NAME, true, { expires: 7 });
    setIsVisible(false);
    trackEvent(APP_DOWNLOAD_MODAL_CLOSED);
  }, []);

  /**
   * Handle continue in browser button click.
   */
  const handleContinueInBrowser = useCallback(() => {
    cookie.set(COOKIE_NAME, true, { expires: 7 });
    setIsVisible(false);
    trackEvent(APP_DOWNLOAD_MODAL_CONTINUE_IN_BROWSER);
  }, []);

  // Extract store URLs to avoid unnecessary callback recreations
  const iosStoreUrl = smartBanner?.props?.['button-url-apple'];
  const androidStoreUrl = smartBanner?.props?.['button-url-google'];

  /**
   * Handle download button click.
   */
  const handleDownloadClick = useCallback(() => {
    cookie.set(COOKIE_NAME, true, { expires: 7 });
    setIsVisible(false);
    trackEvent(APP_DOWNLOAD_MODAL_CONTINUE_IN_APP, { platform });

    // Use setTimeout to ensure cookie is set and modal is closed before navigation
    // This prevents conflicts between the modal closing animation and the new window opening
    setTimeout(() => {
      const storeLinks = {
        ios: iosStoreUrl,
        android: androidStoreUrl,
      };
      const storeUrl = platform === 'ios' ? storeLinks.ios : storeLinks.android;

      // Only open the URL if it exists
      if (storeUrl) {
        window.open(storeUrl, '_blank', 'noopener,noreferrer');
      }
    }, 100);
  }, [platform, iosStoreUrl, androidStoreUrl]);

  const isPromoModal = isPromoDownloadModalActive(promoDownloadModal);
  const modalImageUrl = getAppDownloadModalImageUrl({
    isPromoModal,
    downloadAppImage,
    defaultPromoImage: defaultDiscountModalImage,
    defaultDownloadImage: defaultModalImage,
  });

  if (!shouldShowModal) {
    return null;
  }

  return (
    <Modal
      id={MODAL_ID}
      defaultOpened
      showOverFlowContent
      responsiveSize="auto"
      onCloseModal={closeModal}
      aria-label={t('app_download_modal.title')}
      aria-describedby="app-download-modal-description"
    >
      <div className="relative flex justify-center">
        <div
          className={`absolute bottom-2.5 bg-no-repeat bg-center filter drop-shadow-[-5px_15px_10px_rgba(0,0,0,0.5)] ${
            isPromoModal ? 'w-[280px] h-[160px]' : 'w-[200px] h-[280px]'
          }`}
          style={{
            backgroundImage: `url(${modalImageUrl})`,
            backgroundSize: 'contain',
          }}
        />
      </div>
      <Spacing vertical size="L" responsiveSize="M" alignItems="center">
        <Text size="L" id="app-download-modal-description">
          {isPromoModal ? (
            <PromoDownloadModalMessage promoDownloadModal={promoDownloadModal} t={t} />
          ) : (
            <Trans t={t} i18nKey="app_download_modal.message" as="p">
              <b />
            </Trans>
          )}
        </Text>
        <Button
          fullWidth
          borderRadius="S"
          withHeight
          text={t('app_download_modal.store_cta')}
          onClick={handleDownloadClick}
          buttonType="primary"
          variant="primary"
        />
        <FlatButton text={t('app_download_modal.close_cta')} onClick={handleContinueInBrowser} />
      </Spacing>
    </Modal>
  );
};

AppDownloadModal.propTypes = {
  hideDialog: PropTypes.bool,
};

AppDownloadModal.defaultProps = {
  hideDialog: false,
};

export default AppDownloadModal;
